import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSelector } from "react-redux";
import DynamicTable from "../../../components/lab-dynamic-table";
import DateRangeSelector from "../../../components/DateRangeSelector";

interface ReferringDoctor {
  _id: string;
  doctorName: string;
  referralCommission?: number;
}

interface DoctorShareSummaryRow {
  _id: string;
  doctorName: string;
  phoneNumber?: string;
  shareAmount: number;
  grossAmount?: number;
  discount?: number;
  paidAmount?: number;
  dueAmount?: number;
  patientCount: number;
}

const formatAmount = (value?: number) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const formatDisplayDate = (value?: string) => {
  if (!value) return "-";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DoctorShareSummaryReport: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const collectionCenters = profile?.data?.collectionCenter || [];
  const printedBy =
    profile?.data?.fullName || profile?.data?.firstName || "User";

  const [rows, setRows] = useState<DoctorShareSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] =
    useState<string>("all");
  const [referringDoctors, setReferringDoctors] = useState<ReferringDoctor[]>(
    []
  );
  const [referringDrId, setReferringDrId] = useState<string>("");

  const fetchReferringDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/referring-doctor/getAllBySearch`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setReferringDoctors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching referring doctors:", error);
      setReferringDoctors([]);
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/dashboard/getDoctorShareSummaryAggregated`,
        {
          params: {
            fromDate,
            toDate,
            ...(selectedCollectionCenterId !== "all" && {
              collectionCenterId: selectedCollectionCenterId,
            }),
            ...(referringDrId && { referringDrId }),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        setRows(response.data.data || []);
      } else {
        toast.error(
          response.data?.message || "Failed to fetch doctor share summary"
        );
        setRows([]);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Error fetching doctor share summary"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferringDoctors();
  }, []);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedCollectionCenterId, referringDrId]);

  const handleCollectionCenterChange = (event: SelectChangeEvent) => {
    setSelectedCollectionCenterId(event.target.value);
  };

  const totals = rows.reduce(
    (acc, row) => ({
      shareAmount: acc.shareAmount + Number(row.shareAmount || 0),
      grossAmount: acc.grossAmount + Number(row.grossAmount || 0),
      discount: acc.discount + Number(row.discount || 0),
      paidAmount: acc.paidAmount + Number(row.paidAmount || 0),
      dueAmount: acc.dueAmount + Number(row.dueAmount || 0),
      patientCount: acc.patientCount + Number(row.patientCount || 0),
    }),
    {
      shareAmount: 0,
      grossAmount: 0,
      discount: 0,
      paidAmount: 0,
      dueAmount: 0,
      patientCount: 0,
    }
  );

  const handleExcelExport = () => {
    if (!rows.length) {
      toast.warn("No data to export.");
      return;
    }

    const worksheetData = rows.map(row => ({
      "Doctor Name": row.doctorName || "-",
      "Phone Number": row.phoneNumber || "-",
      "Share Amount": Number(row.shareAmount || 0),
      "Gross Charges": Number(row.grossAmount || 0),
      Discount: Number(row.discount || 0),
      "Paid Amount": Number(row.paidAmount || 0),
      "Due Amount": Number(row.dueAmount || 0),
      "Patient Count": Number(row.patientCount || 0),
    }));

    worksheetData.push({
      "Doctor Name": "TOTAL",
      "Phone Number": "",
      "Share Amount": totals.shareAmount,
      "Gross Charges": totals.grossAmount,
      Discount: totals.discount,
      "Paid Amount": totals.paidAmount,
      "Due Amount": totals.dueAmount,
      "Patient Count": totals.patientCount,
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Doctor Share Summary");
    xlsx.writeFile(
      workbook,
      `Doctor_Share_Summary_Report_${fromDate}_to_${toDate}.xlsx`
    );
  };

  const handlePrint = () => {
    if (!rows.length) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 8;
    let yPosition = margin;

    doc
      .setFontSize(8)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(`Print date: ${new Date().toLocaleString()}`, margin, yPosition);
    doc.text(`Printed by: ${printedBy}`, pageWidth - margin, yPosition, {
      align: "right",
    });

    yPosition += 6;
    doc
      .setFontSize(11)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("Doctor Share Summary", margin, yPosition);
    doc
      .setFontSize(9)
      .setFont("helvetica", "normal")
      .text(
        `( ${formatDisplayDate(fromDate)} To ${formatDisplayDate(toDate)} )`,
        pageWidth - margin,
        yPosition,
        { align: "right" }
      );

    yPosition += 4;
    doc
      .setDrawColor(180, 180, 180)
      .setLineWidth(0.3)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    yPosition += 3;

    const head = [
      [
        "Doctor Name",
        "Phone Number",
        "Share Amount",
        "Gross Charges",
        "Discount",
        "Paid Amount",
        "Due Amount",
        "Patient Count",
      ],
    ];
    const body = rows.map(row => [
      row.doctorName || "-",
      row.phoneNumber || "-",
      formatAmount(row.shareAmount),
      formatAmount(row.grossAmount),
      formatAmount(row.discount),
      formatAmount(row.paidAmount),
      formatAmount(row.dueAmount),
      row.patientCount ?? 0,
    ]);

    body.push([
      "TOTAL",
      "",
      formatAmount(totals.shareAmount),
      formatAmount(totals.grossAmount),
      formatAmount(totals.discount),
      formatAmount(totals.paidAmount),
      formatAmount(totals.dueAmount),
      totals.patientCount,
    ]);

    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
      didParseCell: data => {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [230, 230, 230];
        }
      },
    });

    doc.save(`Doctor_Share_Summary_Report_${fromDate}_to_${toDate}.pdf`);
  };

  const headers = [
    "Doctor Name",
    "Phone Number",
    "Share Amount",
    "Gross Charges",
    "Discount",
    "Paid Amount",
    "Due Amount",
    "Patient Count",
  ];

  const tableData: React.ReactNode[][] = rows.map(row => [
    row.doctorName || "-",
    row.phoneNumber || "-",
    formatAmount(row.shareAmount),
    formatAmount(row.grossAmount),
    formatAmount(row.discount),
    formatAmount(row.paidAmount),
    formatAmount(row.dueAmount),
    row.patientCount ?? 0,
  ]);

  if (rows.length > 0) {
    tableData.push([
      "TOTAL",
      "",
      formatAmount(totals.shareAmount),
      formatAmount(totals.grossAmount),
      formatAmount(totals.discount),
      formatAmount(totals.paidAmount),
      formatAmount(totals.dueAmount),
      totals.patientCount,
    ]);
  }

  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 220 } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Referring Doctor
            </Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={referringDoctors}
              getOptionLabel={option => option.doctorName || ""}
              value={
                referringDoctors.find(doc => doc._id === referringDrId) || null
              }
              onChange={(_event, newValue) => {
                setReferringDrId(newValue?._id || "");
              }}
              renderInput={params => (
                <TextField {...params} placeholder="All doctors" />
              )}
            />
          </Box>

          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Collection Center
            </Typography>
            <FormControl
              className="custom-input"
              size="small"
              fullWidth
              sx={{ minWidth: { xs: "100%", sm: "180px" } }}
            >
              <Select
                value={selectedCollectionCenterId}
                onChange={handleCollectionCenterChange}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {collectionCenters.map((center: any) => (
                  <MenuItem key={center._id} value={center._id}>
                    {center.collectionCenterName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <DateRangeSelector
            compact
            preset="lab"
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={setDateRange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box sx={{ p: 1, maxHeight: "60vh", overflowY: "hidden" }}>
        <DynamicTable
          headers={headers}
          data={tableData}
          loading={loading}
          height={"60vh"}
          totalRows={0}
        />
      </Box>
    </Box>
  );
};

export default DoctorShareSummaryReport;
